import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import LiveClock from '../../components/LiveClock';
import {
  LayoutDashboard, BookOpen, Users, AlertTriangle, ClipboardList,
  Lock, ShieldCheck, MessageSquare, Search as SearchIcon, ArrowLeft,
} from 'lucide-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { biz } from '../../lib/api';
import { useAuth, isBizStaff } from '../../lib/auth';
import { toast } from 'sonner';

const NAV = [
  { to: '/business',           label: 'Dashboard',   icon: LayoutDashboard, end: true, testid: 'biz-nav-dashboard' },
  { to: '/business/daybook',   label: 'Day Book',    icon: BookOpen,        testid: 'biz-nav-daybook' },
  { to: '/business/customers', label: 'Customers',   icon: Users,           testid: 'biz-nav-customers' },
  { to: '/business/complaints',label: 'Complaints',  icon: AlertTriangle,   testid: 'biz-nav-complaints' },
  { to: '/business/followups', label: 'Follow-ups',  icon: ClipboardList,   testid: 'biz-nav-followups' },
  { to: '/business/day-close', label: 'Day Closing', icon: Lock,            testid: 'biz-nav-dayclose' },
  { to: '/business/settings',  label: 'Settings',    icon: ShieldCheck,     testid: 'biz-nav-settings' },
];

export default function BusinessLayout() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [openSearch, setOpenSearch] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (!isBizStaff(user)) return;
    biz.me().then(setMe).catch(() => setMe(null));
  }, [user]);

  const runSearch = useCallback((qv) => {
    if (!qv || qv.length < 2) { setResults(null); return; }
    setSearching(true);
    biz.search(qv).then(setResults).finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  useEffect(() => { setOpenSearch(false); }, [location.pathname]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="biz-signin-gate">
        <div className="font-heading font-black text-2xl">Sign in to access Business Suite</div>
        <p className="text-sm text-ink/60 mt-1">Please log in with your admin or staff account.</p>
        <div className="mt-4 flex justify-center gap-2">
          <button data-testid="biz-signin-btn" className="btn-primary !py-2 !px-4 text-sm"
            onClick={() => window.dispatchEvent(new CustomEvent('mandispot:open-auth', { detail: { mode: 'signin' } }))}>Sign in</button>
          <Link to="/" data-testid="biz-gate-home-link" className="btn-outline !py-2 !px-4 text-sm">Back to BharatCart</Link>
        </div>
      </div>
    );
  }

  if (!isBizStaff(user)) {
    return (
      <div className="max-w-md mx-auto text-center py-16 px-4" data-testid="biz-staff-gate">
        <Lock className="w-10 h-10 mx-auto text-ink/30"/>
        <div className="font-heading font-black text-2xl mt-3">BizDesk is for shop staff</div>
        <p className="text-sm text-ink/60 mt-1">Your account ({user.role}) can't open the business suite. Ask the shop owner to add you as staff.</p>
        <Link to="/" data-testid="biz-staff-gate-home" className="btn-primary !py-2 !px-4 text-sm mt-4 inline-flex">Back to BharatCart</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 md:px-4 pb-24 md:pb-6" data-testid="biz-layout">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-border py-2 -mx-2 md:-mx-4 px-3 md:px-4 flex items-center gap-2">
        <Link to="/" data-testid="biz-back-to-market" title="Back to BharatCart" aria-label="Back to BharatCart"
          className="w-9 h-9 rounded-full border border-border grid place-items-center hover:border-[#E67E22] hover:text-[#D35400]">
          <ArrowLeft className="w-4 h-4"/>
        </Link>
        <div className="font-heading font-black text-lg text-[#0D47A1]">BizDesk</div>
        {me?.business_role && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E3F2FD] text-[#0D47A1]"
            data-testid="biz-role-badge">
            {me.business_role.replace('_', ' ')}
          </span>
        )}
        <LiveClock testid="biz-live-clock" className="hidden sm:inline-flex ml-auto text-[11px] font-bold text-ink/60"/>
        <button data-testid="biz-open-search"
          onClick={() => setOpenSearch(true)}
          className="ml-auto sm:ml-2 pill !bg-white text-xs normal-case tracking-normal sm:min-w-[220px] justify-start">
          <SearchIcon className="w-3 h-3"/> Search customers, complaints…
        </button>
      </header>

      <div className="md:flex md:gap-4 mt-3">
        <aside className="hidden md:block w-56 shrink-0">
          <nav className="space-y-1 sticky top-16">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    isActive ? 'bg-[#E67E22] text-white font-semibold' : 'hover:bg-[#FFF8EE] text-ink'
                  }`
                }>
                <n.icon className="w-4 h-4"/> {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0">
          <Outlet context={{ me }}/>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-border grid grid-cols-6 gap-0" data-testid="biz-mobile-nav">
        {NAV.slice(0, 6).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 text-[10px] ${
                isActive ? 'text-[#E67E22] font-bold' : 'text-ink/60'
              }`
            }>
            <n.icon className="w-4 h-4"/>{n.label}
          </NavLink>
        ))}
      </nav>

      {/* Global search overlay */}
      {openSearch && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-16 px-3"
          data-testid="biz-search-overlay" onClick={() => setOpenSearch(false)}>
          <div className="w-full max-w-2xl bg-white rounded-2xl border border-border shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-border flex items-center gap-2">
              <SearchIcon className="w-4 h-4 text-ink/60"/>
              <input autoFocus data-testid="biz-search-input"
                placeholder="Customer, mobile, complaint no, order no, invoice, serial, keyword…"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="flex-1 outline-none text-sm"/>
              {searching && <span className="text-xs text-ink/50">searching…</span>}
            </div>
            <div className="max-h-[60vh] overflow-auto p-3 text-sm">
              {!results && <div className="text-ink/50 text-xs">Type at least 2 characters</div>}
              {results && (
                <div className="grid gap-3">
                  <ResultBlock title="Complaints" items={results.complaints} link={(i) => `/business/complaints/${i.complaint_number}`}
                    label={(i) => `${i.complaint_number} — ${i.customer_name}`}
                    hint={(i) => `${i.product || ''} · ${i.status} · ${i.priority}`}
                    testidPrefix="biz-search-complaint" onClick={() => setOpenSearch(false)}/>
                  <ResultBlock title="Customers" items={results.customers} link={(i) => `/business/customers/${i.id}`}
                    label={(i) => `${i.name}`} hint={(i) => `${i.customer_number} · ${i.mobile}`}
                    testidPrefix="biz-search-customer" onClick={() => setOpenSearch(false)}/>
                  <ResultBlock title="Activities" items={results.activities} link={() => `/business/daybook`}
                    label={(i) => `${i.activity_number} — ${i.customer_name || '—'}`}
                    hint={(i) => `${i.activity_type} · ${i.status} · ${i.date}`}
                    testidPrefix="biz-search-activity" onClick={() => setOpenSearch(false)}/>
                  <ResultBlock title="Follow-ups" items={results.followups} link={() => `/business/followups`}
                    label={(i) => `${i.followup_number} — ${i.customer_name || '—'}`}
                    hint={(i) => `Due ${i.due_date} · ${i.status}`}
                    testidPrefix="biz-search-followup" onClick={() => setOpenSearch(false)}/>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultBlock({ title, items, link, label, hint, testidPrefix, onClick }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink/50 mb-1">{title} · {items.length}</div>
      <div className="grid gap-1">
        {items.map((i, idx) => (
          <NavLink key={i.id || idx} to={link(i)} onClick={onClick}
            data-testid={`${testidPrefix}-${idx}`}
            className="border border-border rounded-lg px-2 py-1.5 hover:bg-[#FFF8EE] text-sm">
            <div className="font-semibold text-ink truncate">{label(i)}</div>
            {hint && <div className="text-xs text-ink/60 truncate">{hint(i)}</div>}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

/* Shared helpers used by all business pages */
export function StatusBadge({ value }) {
  const map = {
    NEW: 'bg-[#FFF3E0] text-[#EF6C00]',
    ACKNOWLEDGED: 'bg-[#E3F2FD] text-[#0D47A1]',
    ASSIGNED: 'bg-[#E3F2FD] text-[#0D47A1]',
    UNDER_INVESTIGATION: 'bg-[#FFFDE7] text-[#795548]',
    ACTION_REQUIRED: 'bg-[#FFF3E0] text-[#EF6C00]',
    CUSTOMER_CONFIRMATION: 'bg-[#E1F5FE] text-[#01579B]',
    RESOLVED: 'bg-[#E8F5E9] text-[#1B5E20]',
    CLOSED: 'bg-[#ECEFF1] text-[#455A64]',
    ON_HOLD: 'bg-[#F3E5F5] text-[#6A1B9A]',
    ESCALATED: 'bg-[#FFEBEE] text-[#B71C1C]',
    REOPENED: 'bg-[#FFF3E0] text-[#EF6C00]',
    CANCELLED: 'bg-[#ECEFF1] text-[#455A64]',
    pending: 'bg-[#FFF3E0] text-[#EF6C00]',
    in_progress: 'bg-[#E3F2FD] text-[#0D47A1]',
    completed: 'bg-[#E8F5E9] text-[#1B5E20]',
    cancelled: 'bg-[#ECEFF1] text-[#455A64]',
  };
  const cls = map[value] || 'bg-[#F5F5F5] text-ink/70';
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${cls}`} data-testid={`biz-status-${value}`}>{(value || '').replace(/_/g, ' ')}</span>;
}

export function PriorityBadge({ value }) {
  const cls = {
    critical: 'bg-[#FFEBEE] text-[#B71C1C]',
    high: 'bg-[#FFF3E0] text-[#EF6C00]',
    medium: 'bg-[#FFFDE7] text-[#795548]',
    low: 'bg-[#E8F5E9] text-[#1B5E20]',
  }[value] || 'bg-[#F5F5F5]';
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${cls}`}>{value}</span>;
}

export function Card({ title, value, tone = 'default', testid }) {
  const tones = {
    default: 'text-ink',
    warn: 'text-[#EF6C00]',
    bad: 'text-[#B71C1C]',
    good: 'text-[#1B5E20]',
    info: 'text-[#0D47A1]',
  };
  return (
    <div className="rounded-2xl border border-border bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink/50">{title}</div>
      <div className={`font-heading font-black text-2xl tabular-nums mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}
